import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocalizedText } from '../../lib/localizedText'
import { exportCriteriaScoreRanges } from './api/criteriaScoreRangeApi'
import DataTable from '../../components/ui/DataTable'
import EmptyState from '../../components/ui/EmptyState'
import ExcelExportButton from '../../components/ui/ExcelExportButton'
import PageHeader from '../../components/ui/PageHeader'
import RefreshButton from '../../components/ui/RefreshButton'
import { useToast } from '../../components/ui/ToastProvider'
import { isOptimisticLock } from '../../api/apiError'
import { criteriaScoreRangeColumns } from './components/CriteriaScoreRangeColumns'
import CriteriaScoreRangeFormDialog, {
  type CriteriaScoreRangeFormPayload,
} from './components/CriteriaScoreRangeFormDialog'
import { useCriteriaScoreRanges } from './hooks/useCriteriaScoreRanges'
import type { CriteriaScoreRange } from './types'
import Button from '../../components/ui/Button'
import { CARD_VIEW_BREAKPOINT, useMediaQuery } from '../../lib/useMediaQuery'
// Layout sınıfları (admin-page/admin-form/admin-row-actions) paylaşılan — 012'den yeniden kullanılır.
import '../admin/admin.css'

/**
 * Etkinlik Takip → Değerlendirme Skoru yönetim ekranı (027). **Salt liste + düzenle**: paylaşılan
 * ui/DataTable (sayfalama kapalı, sabit 3 bant) + ui/FormDialog (düzenle). "Yeni ekle" / sil / durum
 * toggle / ↑↓ sıralama **yoktur**. Başlık = menü adı (`nav.etkinlik.criteriaScoreRange`).
 */
export default function CriteriaScoreRangesPage() {
  const { t } = useTranslation()
  const localized = useLocalizedText()
  const { list, update } = useCriteriaScoreRanges()
  const toast = useToast()

  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<CriteriaScoreRange | null>(null)

  const rows = list.data?.content ?? []
  const isMobile = useMediaQuery(CARD_VIEW_BREAKPOINT)

  const notify = (severity: 'success' | 'error', summary: string) =>
    toast.show({ severity, summary, life: 3000 })

  const openEdit = (row: CriteriaScoreRange) => {
    setEditing(row)
    setFormOpen(true)
  }

  const onSubmit = (payload: CriteriaScoreRangeFormPayload) => {
    if (!editing) return
    update.mutate(
      { id: editing.id, body: { ...payload, modDate: editing.modDate } },
      {
        onSuccess: () => {
          notify('success', t('activity.criteriaScoreRange.toast.updated'))
          setFormOpen(false)
        },
        onError: (err) => {
          // Eşzamanlı düzenleme çakışması → formu kapat + listeyi tazele (merkezî toast 019 basar).
          if (isOptimisticLock(err)) {
            setFormOpen(false)
            list.refetch()
          }
        },
      },
    )
  }

  return (
    <div className="admin-page">
      <CriteriaScoreRangeFormDialog
        visible={formOpen}
        initial={editing}
        submitting={update.isPending}
        onSubmit={onSubmit}
        onHide={() => setFormOpen(false)}
      />

      <PageHeader
        title={t('nav.etkinlik.criteriaScoreRange')}
        description={t('activity.criteriaScoreRange.description')}
        actions={
          <>
            <RefreshButton onRefresh={() => list.refetch()} />
            <ExcelExportButton onExport={exportCriteriaScoreRanges} disabled={rows.length === 0} />
          </>
        }
      />

      {/* Sabit 3 bant → sayfalama kapalı; kolon-sıralaması yok; liste daima SORT_INDEX düzeninde. */}
      {isMobile ? (
        <div className="ui-card-list">
          {list.isLoading &&
            Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="ui-card ui-card--skeleton" aria-hidden />
            ))}
          {!list.isLoading && rows.length === 0 && (
            <EmptyState
              title={t('activity.criteriaScoreRange.empty.title')}
              description={t('activity.criteriaScoreRange.empty.desc')}
            />
          )}
          {!list.isLoading &&
            rows.map((row) => (
              <div key={row.id} className="ui-card">
                <div className="ui-card-top">
                  <div className="ui-card-row">
                    <span>{row.startScore}</span>
                    <span>–</span>
                    <span>{row.endScore}</span>
                  </div>
                  <div className="ui-card-actions ui-card-actions--compact">
                    <Button
                      icon="pi pi-pencil"
                      rounded
                      text
                      aria-label={t('activity.criteriaScoreRange.actions.edit')}
                      onClick={() => openEdit(row)}
                    />
                  </div>
                </div>
                <div className="ui-card-head">
                  <span className="ui-card-title">{localized(row.nameTr, row.nameEn)}</span>
                </div>
                
                {row.descriptionTr && <span className="ui-card-row">{row.descriptionTr}</span>}
              </div>
            ))}
        </div>
      ) : (
      <DataTable
        value={rows}
        loading={list.isLoading}
        paginator={false}
        emptyMessage={
          <EmptyState
            title={t('activity.criteriaScoreRange.empty.title')}
            description={t('activity.criteriaScoreRange.empty.desc')}
          />
        }
      >
        {criteriaScoreRangeColumns({ t, localized, onEdit: openEdit })}
      </DataTable>
      )}
    </div>
  )
}
